//@ts-check
import { getCsrfToken } from "common/utils/rails";
import { debugToast } from "../debug-toast";
import { mustBeHtmlElement } from "common/dom-utils";
import { mustBePresent } from "common/utils/blank";
import { PurchasingSectionComponent } from "../purchasing-section/PurchasingSectionComponent";
import { FontsSelector } from "../fonts-selector/FontsSelector";

export class ServerSync {
  /** @type {HTMLElement} */
  element;
  /** @type {import("./LicensingSelector").LicensingSelector} */
  licensingSelector;

  constructor(element, licensingSelector) {
    this.element = element;
    this.licensingSelector = licensingSelector;

    this.purchasingSection = PurchasingSectionComponent.getInstance();
  }
  // --- Configuration from backend ---

  get selectOptionsPath() {
    // Set by app/assets/javascripts/store/views/font_families/show.js.erb
    // @ts-ignore
    return mustBePresent(window.selectOptionsPath, "selectOptionsPath");
  }

  async syncToServer() {
    console.log("Saving licensing options from selection");
    debugToast("Saving licensing options from selection");
    const requestId = (this._requestId || 0) + 1;
    this._requestId = requestId;
    this.element.dataset.licensingSyncState = "pending";

    try {
      const payload = this.getPayloadForServer();
      const responsePayload = await this.save(payload);
      if (requestId === this._requestId && responsePayload) {
        this.handleResponse(responsePayload);
      }
    } finally {
      if (requestId === this._requestId) {
        this.element.dataset.licensingSyncState = "idle";
      }
    }
  }

  /**
   * Collects the selected licensing options for each medium and prepares the payload
   * to be sent to the server.
   *
   * @returns {Object} Payload in the format:
   *     {
   *       "medium_id_1": ["option_id_1", "option_id_2"],
   *       "medium_id_2": ["option_id_3"],
   *     }
   */
  getPayloadForServer() {
    const payload = { licensing_options_data: {} };
    Object.values(this.licensingSelector.mediums).forEach((mediumRow) => {
      if (mediumRow.isSelected) {
        payload.licensing_options_data[mediumRow.id] =
          mediumRow.selectedOptionIds;
      }
    });

    const purchaseSection = mustBeHtmlElement(
      this.element.closest(".purchase-section"),
      "purchase section",
    );
    payload.font_family_id = mustBePresent(
      purchaseSection.dataset.fontFamilyId,
      "data-font-family-id",
    );

    // Branding license owner (only present on pages that render the Branding medium).

    if (this.licensingSelector.usageSelector) {
      payload.license_owner_type =
        this.licensingSelector.usageSelector.ownerType;
      payload.license_owner_name =
        this.licensingSelector.usageSelector.ownerName;
    }

    return payload;
  }

  async save(payload) {
    console.log("Saving licensing options to server:", payload);

    // Rails action: store/licensing#select_options
    const response = await fetch(this.selectOptionsPath, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": getCsrfToken(),
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      console.error("Failed to save licensing options:", response.statusText);
      return;
    }

    const data = await response.json();
    console.log("Licensing options saved successfully:", data);

    return data;
  }

  // --- Handle response from server ---

  handleResponse(payload) {
    // Data from Rails action: store/licensing#select_options
    console.log("Handling server response:", payload);
    debugToast("Handling server response for licensing options");
    this.updateErrorMessages(payload.licensing?.errors);

    this.purchasingSection.updateFromPayload(payload);
  }

  updateErrorMessages(errors) {
    // Show errors as a blocking overlay on the fonts selector so users
    // can't add to cart while the licensing configuration is invalid.
    FontsSelector.getInstance().showDisabledMessage(errors || []);
  }
}
